import * as _ from 'underscore';
import * as $ from 'jquery';

import { config } from '../config';

import BaseStore from '../../../../_common/platform/ts/BaseStore';
import { apiCall } from '../../../../_common/platform/ts/AqlUtility';
import { Constants } from '../../../../_common/platform/ts/Constants';
import * as Utilities from '../../../../_common/platform/ts/Utilities';

import { AppDispatcher as Dispatcher } from '../dispatcher/AppDispatcher';
import { Action, AppActions, I, check as checkAction } from '../actions/AppActions';

import * as Common from '../../tsx/menus/Common';

import { IGlob } from './../Glob';

export interface IWorkspacesDirsData {
    [instanceId: string]: IWorkspacesDirsInstanceState;
}

export interface IWorkspacesDirsStoreState {
    data: IWorkspacesDirsData;

    workspacesInfo: {
        [id: number]: {
            loading: boolean;
            folderId?: number;
            name?: string;
        };
    };

    favoritesHash?: IFavoritesHash;
}

export interface IWorkspaceItem {
    wsid: number;
    name: string;
}

export interface IFavoritesHash {
    [id: number]: IWorkspaceItem;
}

export interface IDirContentItem extends IWorkspaceItem {
    dateCreated: Date;
    dateUpdated?: Date;
    owner?: string;
    updatedBy?: string;
}

export interface IRecentSectionItem extends IWorkspaceItem {
    dirid: number;
    dirpath: string[];
}

export interface IDirContentHash {
    [folderID: string]: IDirContentItem[];
}

export interface ILoadingFolders {
    [folderID: string]: boolean;
}

export interface IWorkspacesDirsInstanceState {
    loading: boolean;
    loadingFolders: ILoadingFolders;
    changingWorkspaces?: { [wsID: number]: boolean };

    errors: any[];

    dirContentDict: { [folderID: string]: IDirContentItem };
    dirContentHash: IDirContentHash;
    displayDirId: string | number;
}

export class WorkspacesDirsStore extends BaseStore<IWorkspacesDirsStoreState, AppActions, Dispatcher, IGlob> {
    public _state: IWorkspacesDirsStoreState;
    public static _name = 'workspaces-dirs';

    public static getInitialState(): IWorkspacesDirsStoreState {
        return {
            data: {},
            workspacesInfo: {}
        };
    }

    private static getInitialInstanceState(displayDirId = config.WIDGETS_ROOT_DIR_ID): IWorkspacesDirsInstanceState {
        return {
            loading: false,
            loadingFolders: {},
            changingWorkspaces: {},

            errors: [],

            dirContentDict: {},
            dirContentHash: {},
            displayDirId: displayDirId
        };
    }

    constructor(glob: IGlob, dispatcher: Dispatcher, actions: AppActions) {
        super(glob, dispatcher, actions, WorkspacesDirsStore._name);

        this._state = _.extend(WorkspacesDirsStore.getInitialState(), this._state);
    }

    public getInstanceState(instanceID: string, displayDirID?: number): IWorkspacesDirsInstanceState {
        let state = this._state.data[instanceID];

        if (state == null) {
            state = this._state.data[instanceID] = WorkspacesDirsStore.getInitialInstanceState(displayDirID);
        }

        return state;
    }

    private loadRecent(def: JQueryDeferred<any>) {
        apiCall(this.actions, 'workspace_GetRecent', {}, null, def);
    }

    public loadDirContent(id: string | number, def?: JQueryDeferred<any>) {
        const dirID = typeof id === 'string' ? parseInt(id, 10) : id;

        return apiCall<IDirContentItem[]>(this.actions, 'workspace_GetDirContent', { dirid: dirID }, null, def);
    }

    private loadFavorites(def: JQueryDeferred<any>) {
        apiCall(this.actions, 'workspace_GetFavorites', {}, null, def);

        def.done((resp) => {
            this._state.favoritesHash = _.reduce<IWorkspaceItem[], IFavoritesHash>(
                resp.result,
                (hash, obj) => {
                    hash[obj.wsid] = obj;
                    return hash;
                },
                {}
            );
        });

        return def;
    }

    public isFavorite(wsID: number) {
        return this._state.favoritesHash != null && this._state.favoritesHash[wsID] != null;
    }

    private loadContent(id: string | number, def: JQueryDeferred<any>) {
        switch (id) {
            case Common.RECENT:
                this.loadRecent(def);
                break;

            case Common.MY_FOLDER:
                this.loadDirContent(this.glob.AuthStore._state.homeFolderId, def);
                break;

            case Common.FAVORITES:
                this.loadFavorites(def);
                break;

            default:
                this.loadDirContent(id, def);
        }

        return def;
    }

    public loadSingleDirContent(
        dirID: string | number,
        instanceID: string,
        displayDirID?: number
    ): JQueryDeferred<any> {
        if (dirID == null) {
            return;
        }

        const result = $.Deferred<any>();
        const folderIDStr = typeof dirID !== 'string' ? dirID.toString() : dirID;

        let instanceState = this._state.data[instanceID];

        if (instanceState == null) {
            instanceState = this._state.data[instanceID] = WorkspacesDirsStore.getInitialInstanceState(displayDirID);
        }

        instanceState.loadingFolders[folderIDStr] = instanceState.loading = true;

        this.loadContent(dirID, result);

        result.done((resp: { result: IDirContentItem[] }) => {
            instanceState.dirContentHash[folderIDStr] = Utilities.sortByName<IDirContentItem>(resp.result);

            _.each(resp.result, (item) => {
                instanceState.dirContentDict[item.wsid] = item;
            });
        });

        result.fail((resp, cancel: boolean) => {
            if (cancel === true) {
                return;
            }

            instanceState.dirContentHash[folderIDStr] = [];
            instanceState.dirContentDict = {};
        });

        result.always(() => {
            this.actions.workspacesExplorerSingleLoadFinish(dirID, instanceID);
        });

        return result;
    }

    private loadSingleDirContentFinish(dirID: string | number, instanceID: string) {
        const instanceState = this._state.data[instanceID];

        if (instanceState == null) {
            return;
        }

        const folderIDStr = typeof dirID !== 'string' ? dirID.toString() : dirID;

        instanceState.loadingFolders[folderIDStr] = instanceState.loading = false;
    }

    private setDisplayId(id, instanceId: string) {
        const instanceState = this._state.data[instanceId];
        if (instanceState == null) {
            return;
        }

        instanceState.displayDirId = id;
    }

    private clearState(instanceId: string, displayDirId?) {
        if (this._state.data[instanceId] == null) {
            return;
        }

        this._state.data[instanceId] = WorkspacesDirsStore.getInitialInstanceState(displayDirId);
    }

    private renameWorkspace(id: number, newName: string, instanceID: string, favouritesOrigin: boolean) {
        const instanceState = this._state.data[instanceID];
        if (instanceState == null) {
            return;
        }

        instanceState.changingWorkspaces[id] = true;

        const methodName = favouritesOrigin ? 'workspace_RenameFavorite' : 'workspace_Rename';

        const def = apiCall(this.actions, methodName, {
            name: newName,
            wsid: id
        });

        def.done((resp) => {
            const dirContent = instanceState.dirContentDict[id];
            if (dirContent != null) {
                dirContent.name = newName;
            }

            _.each(instanceState.dirContentHash, (items) => {
                _.each(items, (item) => {
                    if (item.wsid === id) {
                        item.name = newName;
                    }
                });
            });

            delete instanceState.changingWorkspaces[id];

            this.actions.workspaceSaveFinish(true, id, null, newName, null, null, favouritesOrigin);
            this.actions.showNotification_Success(
                'Workspace "' + id + '" was successfully renamed to "' + newName + '"'
            );
        });

        def.fail(() => {
            delete instanceState.changingWorkspaces[id];

            //this.actions.workspaceSaveFinish(false, id, null, newName, null, null, favouritesOrigin);
            //LL: 27.12.21: newName = null
            this.actions.workspaceSaveFinish(false, id, null, null, null, null, favouritesOrigin);
            this.actions.showNotification_Fail('Workspace "' + id + '" was not renamed');
        });
    }

    private workspaceDelete(id: number, instanceId: string) {
        const instanceState = this._state.data[instanceId];
        if (instanceState == null) {
            return;
        }

        instanceState.changingWorkspaces[id] = true;
    }

    private workspaceDeleteFinish(success: boolean, id: number, instanceId: string) {
        const instanceState = this._state.data[instanceId];
        if (instanceState == null) {
            return;
        }

        delete instanceState.changingWorkspaces[id];

        if (success !== true) {
            return;
        }

        for (let folderID in instanceState.dirContentHash) {
            this.removeWSFromCollection(id, folderID, instanceState);
        }
    }

    private removeWSFromCollection(
        id: number,
        folderID: string | number,
        instanceState: IWorkspacesDirsInstanceState
    ): IDirContentItem {
        const items = getDirContentFromHash(folderID, instanceState.dirContentHash);
        if (items != null) {
            const result = _.find(items, (obj) => {
                return obj.wsid === id;
            });

            if (result != null) {
                items.splice(_.indexOf(items, result), 1);
            }

            return result;
        }

        return undefined;
    }

    private workspaceMove(
        id: number,
        moveFromFolderID: number,
        moveToFolderID: number,
        instanceID: string,
        overwrite: boolean
    ) {
        if (moveFromFolderID === moveToFolderID) {
            return;
        }

        const instanceState = this._state.data[instanceID];
        if (instanceState == null) {
            return;
        }

        const def = apiCall(this.actions, 'workspace_Move', {
            wsid: id,
            overwrite: overwrite,
            NewDirID: moveToFolderID
        });

        instanceState.changingWorkspaces[id] = true;

        def.done(() => {
            const removedItem = this.removeWSFromCollection(id, moveFromFolderID, instanceState);

            if (removedItem != null) {
                const moveToIDStr = moveToFolderID.toString();
                const items = instanceState.dirContentHash[moveToIDStr];

                if (items != null) {
                    items.push(removedItem);
                    instanceState.dirContentHash[moveToIDStr] = Utilities.sortByName(items);
                }

                this.actions.showNotification_Success(
                    'Workspace "' + id + '" was successfully moved to the folder "' + moveToIDStr + '"'
                );
            }
        });

        def.always((obj) => {
            delete instanceState.changingWorkspaces[id];

            this.actions.workspaceMoveFinish(id, moveFromFolderID, moveToFolderID, instanceID);
        });
    }

    private workspaceFavouritesAdd(id: number, name: string, instanceID: string) {
        const def = apiCall(this.actions, 'workspace_AddFavorite', {
            wsid: id,
            name: name
        });

        def.done((obj) => {
            this._state.favoritesHash[id] = {
                wsid: id,
                name: name
            };

            if (instanceID != null) {
                const instanceState = this._state.data[instanceID];

                if (instanceState != null) {
                    const items = instanceState.dirContentHash[Common.FAVORITES];

                    if (items != null) {
                        if (
                            _.find(items, (item) => {
                                return item.wsid === id;
                            }) == null
                        ) {
                            items.push({
                                wsid: id,
                                name: name,
                                dateCreated: new Date()
                            });
                        }
                    }
                }
            }

            this.actions.showNotification_Success('Workspace "' + id + '" was successfully added to favourites');
        });

        // def.fail((obj) => {
        // });

        def.always((resp) => {
            this.actions.workspaceFavouritesAddFinish(id, name, instanceID);
        });
    }

    private workspaceFavouritesRemove(id: number, instanceID: string) {
        const def = apiCall(this.actions, 'workspace_DelFavorite', {
            wsid: id
        });

        def.done((obj) => {
            delete this._state.favoritesHash[id];

            if (instanceID != null) {
                const instanceState = this._state.data[instanceID];
                if (instanceState != null) {
                    this.removeWSFromCollection(id, Common.FAVORITES, instanceState);
                }
            }

            this.actions.showNotification_Success('Workspace "' + id + '" was successfully removed from favourites');
        });

        // def.fail((obj) => {
        // });

        def.always((resp) => {
            this.actions.workspaceFavouritesRemoveFinish(id);
        });
    }

    private workspaceGetInfo(ids: number[]) {
        _.each(ids, (id) => (this._state.workspacesInfo[id] = { loading: true }));

        $.when
            .apply(
                $,
                _.map(ids, (id) => {
                    return apiCall(this.actions, 'workspace_GetInfo', {
                        wsid: id
                    });
                })
            )
            .done((...results) => {
                _.each(
                    _.zip(ids, results),
                    ([
                        id,
                        {
                            result: { dirid, name }
                        }
                    ]) => {
                        this._state.workspacesInfo[id] = {
                            loading: false,
                            folderId: dirid,
                            name
                        };
                    }
                );
            })
            .always(() => {
                this.glob.AppActions.workspaceGetInfoFinish(ids);
            });
    }

    private workspaceGetInfoFinish(ids: number[]) {
        _.each(ids, (id) => (this._state.workspacesInfo[id].loading = false));
    }

    protected eventsHandler(payload) {
        if (!super.eventsHandler(payload)) {
            return false;
        }

        const a: Action = payload.action;
        const _case = checkAction;

        do {
            if (_case<I.WORKSPACES_EXPLORER_CLEAR>(a, Constants.WORKSPACES_EXPLORER_CLEAR)) {
                this.clearState(a.instanceId);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACES_EXPLORER_SINGLE_LOAD>(a, Constants.WORKSPACES_EXPLORER_SINGLE_LOAD)) {
                this.loadSingleDirContent(a.id, a.instanceId);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACES_EXPLORER_SINGLE_LOAD_FINISH>(a, Constants.WORKSPACES_EXPLORER_SINGLE_LOAD_FINISH)) {
                this.loadSingleDirContentFinish(a.id, a.instanceId);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACES_EXPLORER_SET_DISPLAY_ID>(a, Constants.WORKSPACES_EXPLORER_SET_DISPLAY_ID)) {
                this.setDisplayId(a.id, a.instanceId);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_DIRS_RENAME>(a, Constants.WORKSPACE_DIRS_RENAME)) {
                this.renameWorkspace(a.id, a.newName, a.instanceID, a.favouritesOrigin);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_SAVE_FINISH>(a, Constants.WORKSPACE_SAVE_FINISH)) {
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_DELETE>(a, Constants.WORKSPACE_DELETE)) {
                this.workspaceDelete(a.id, a.instanceId);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_DELETE_FINISH>(a, Constants.WORKSPACE_DELETE_FINISH)) {
                this.workspaceDeleteFinish(a.success, a.id, a.instanceId);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_MOVE>(a, Constants.WORKSPACE_MOVE)) {
                this.workspaceMove(a.id, a.moveFromFolderID, a.moveToFolderID, a.instanceID, a.overwrite);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_MOVE_FINISH>(a, Constants.WORKSPACE_MOVE_FINISH)) {
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_FAVOURITES_ADD>(a, Constants.WORKSPACE_FAVOURITES_ADD)) {
                this.workspaceFavouritesAdd(a.id, a.name, a.instanceID);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_FAVOURITES_ADD_FINISH>(a, Constants.WORKSPACE_FAVOURITES_ADD_FINISH)) {
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_FAVOURITES_REMOVE>(a, Constants.WORKSPACE_FAVOURITES_REMOVE)) {
                this.workspaceFavouritesRemove(a.id, a.instanceID);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_FAVOURITES_REMOVE_FINISH>(a, Constants.WORKSPACE_FAVOURITES_REMOVE_FINISH)) {
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_GET_INFO>(a, Constants.WORKSPACE_GET_INFO)) {
                this.workspaceGetInfo(a.ids);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_GET_INFO_FINISH>(a, Constants.WORKSPACE_GET_INFO_FINISH)) {
                this.workspaceGetInfoFinish(a.ids);
                this.emitChange();
                break;
            }
        } while (false);

        return true;
    }

    static get(glob, dispatcher: Dispatcher, actions: AppActions) {
        return new WorkspacesDirsStore(glob, dispatcher, actions);
    }
}

export function getDirContentFromHash<THash = IDirContentHash>(dirID: string | number, hash: THash) {
    if (dirID == null) {
        return undefined;
    }

    const folderIDStr = typeof dirID !== 'string' ? dirID.toString() : dirID;
    return hash[folderIDStr];
}

export function isFolderLoading(dirID: string | number, loadingFolders: ILoadingFolders) {
    if (dirID == null) {
        return undefined;
    }

    const folderIDStr = typeof dirID !== 'string' ? dirID.toString() : dirID;
    return loadingFolders[folderIDStr];
}
